import { useEffect, useMemo, useState } from "react";
import {
  AppstoreOutlined,
  CustomerServiceOutlined,
  SafetyCertificateOutlined,
  TruckOutlined
} from "@ant-design/icons";
import { Empty } from "antd";

import axiosInstance from "../../utils/axios";
import {
  readHomeCache,
  writeHomeCache
} from "../../utils/homeCache";

import {
  getAllIndexPage,
  getRandomIndexPage
} from "../../api/category";

import { getAllBlog } from "../../api/blog";

import ItemProduct from "../../section/itemProduct";
import ItemProductGrid from "../../section/itemProductGrid";
import ItemBlog from "../../section/itemBlog";

import nutritionIcon from "../../assets/demo/categories/nutrition.svg";
import rehabIcon from "../../assets/demo/categories/rehab.svg";
import medicalIcon from "../../assets/demo/categories/medical.svg";
import supplyIcon from "../../assets/demo/categories/supply.svg";
import elderlyIcon from "../../assets/demo/categories/elderly.svg";
import booksIcon from "../../assets/demo/categories/books.svg";
import promoIcon from "../../assets/demo/categories/promo.svg";
import courseIcon from "../../assets/demo/categories/course.svg";

import "./mobileV3.css";
import "./homeV6.css";
import "./homeFull.css";

const normalize = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const categoryIcon = (name = "") => {
  const n = normalize(name);

  if (n.includes("dinh duong")) return nutritionIcon;
  if (n.includes("phuc hoi")) return rehabIcon;
  if (n.includes("dung cu y khoa")) return medicalIcon;
  if (n.includes("vat tu")) return supplyIcon;
  if (n.includes("thiet bi")) return medicalIcon;
  if (n.includes("nguoi lon tuoi")) return elderlyIcon;

  if (n.includes("sach") || n.includes("tai lieu")) {
    return booksIcon;
  }

  if (
    n.includes("voucher") ||
    n.includes("khuyen mai") ||
    n.includes("ma giam")
  ) {
    return promoIcon;
  }

  if (n.includes("khoa hoc")) return courseIcon;

  return medicalIcon;
};

const shortCategoryName = (name = "") => {
  const n = normalize(name);

  if (n.includes("dinh duong")) return "Dinh dưỡng";
  if (n.includes("phuc hoi")) return "Phục hồi chức năng";
  if (n.includes("dung cu y khoa")) return "Dụng cụ y khoa";
  if (n.includes("vat tu")) return "Vật tư y tế";
  if (n.includes("thiet bi")) return "Thiết bị y tế";
  if (n.includes("nguoi lon tuoi")) return "Người lớn tuổi";

  if (n.includes("sach") || n.includes("tai lieu")) {
    return "Sách & tài liệu";
  }

  if (
    n.includes("voucher") ||
    n.includes("khuyen mai") ||
    n.includes("ma giam")
  ) {
    return "Khuyến mãi";
  }

  if (n.includes("khoa hoc")) return "Khóa học";

  return name;
};

const isBookSection = (section) => {
  const text = normalize(
    [
      section?.category_index_name,
      ...(section?.product || [])
        .slice(0, 6)
        .map((item) => item?.product_name)
    ].join(" ")
  );

  return (
    text.includes("sach") ||
    text.includes("tai lieu") ||
    text.includes("book") ||
    text.includes("cam nang")
  );
};


const isLikelyBookProduct = (item) => {
  const text = normalize(
    [
      item?.product_name,
      item?.product_slug
    ].join(" ")
  );

  return (
    text.includes("sach") ||
    text.includes("tai lieu") ||
    text.includes("cam nang") ||
    text.includes("giao trinh") ||
    text.includes("ebook") ||
    text.includes("textbook")
  );
};

const uniqueProducts = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const key =
      item?.product_id ||
      item?.product_slug ||
      item?.product_name;

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const uniqueCategories = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const label =
      shortCategoryName(
        item?.category_name
      );

    const key = normalize(label);

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const isPromotionSection = (section) => {
  const text = normalize(section?.category_index_name);

  return (
    text.includes("khuyen mai") ||
    text.includes("giam gia") ||
    text.includes("uu dai") ||
    text.includes("sale")
  );
};


const newestTime = (item) => {
  const raw =
    item?.create_at ||
    item?.created_at ||
    item?.createdAt ||
    item?.update_at ||
    item?.updated_at ||
    item?.updatedAt;

  if (!raw) return 0;

  const time = new Date(raw).getTime();

  return Number.isFinite(time)
    ? time
    : 0;
};

const newestProducts = (items = []) =>
  [...items].sort((a, b) => {
    const dateDiff =
      newestTime(b) -
      newestTime(a);

    if (dateDiff !== 0) {
      return dateDiff;
    }

    return (
      Number(b?.product_id || 0) -
      Number(a?.product_id || 0)
    );
  });

const newestBlogs = (items = []) =>
  [...items].sort((a, b) => {
    const dateDiff =
      newestTime(b) -
      newestTime(a);

    if (dateDiff !== 0) {
      return dateDiff;
    }

    return (
      Number(b?.blog_id || 0) -
      Number(a?.blog_id || 0)
    );
  });

const bannerImage = (banner) =>
  banner?.index_page_image || "";

const bannerLink = (banner) =>
  banner?.index_page_link || "/danh-muc-san-pham";

export default function HomeIsMobile() {
  const [category, setCategory] = useState(
    () => readHomeCache("category", [])
  );
  const [categoryIndex, setCategoryIndex] = useState(
    () => readHomeCache("categoryIndex", [])
  );
  const [remoteBanner, setRemoteBanner] = useState(
    () => readHomeCache("remoteBanner", [])
  );
  const [randomIndex, setRandomIndex] = useState(
    () => readHomeCache("randomIndexMobile", [])
  );
  const [blog, setBlog] = useState(
    () => readHomeCache("blog", [])
  );
  const [itemsToShow, setItemsToShow] = useState(8);
  const [saleClock, setSaleClock] = useState("00:00:00");
  const [fullFlashSale, setFullFlashSale] = useState(
    () => readHomeCache("fullFlashSale", [])
  );

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();

      const end = new Date(now);
      end.setHours(23, 59, 59, 999);

      const diff = Math.max(
        0,
        end.getTime() - now.getTime()
      );

      const hours = Math.floor(
        diff / 3600000
      );

      const minutes = Math.floor(
        (diff % 3600000) / 60000
      );

      const seconds = Math.floor(
        (diff % 60000) / 1000
      );

      const pad = (value) =>
        String(value).padStart(2, "0");

      setSaleClock(
        `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      );
    };

    updateClock();

    const timer =
      window.setInterval(
        updateClock,
        1000
      );

    return () =>
      window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;

    const applyCache = (
      key,
      setter,
      value
    ) => {
      if (!active) return;

      setter(value);
      writeHomeCache(key, value);
    };

    axiosInstance
      .get("/category/getAllHome")
      .then((res) => {
        applyCache(
          "category",
          setCategory,
          res?.data || { data: [] }
        );
      })
      .catch(() => {});

    axiosInstance
      .get("/index-page/getAllIndexPage")
      .then((res) => {
        applyCache(
          "remoteBanner",
          setRemoteBanner,
          res?.data?.data || []
        );
      })
      .catch(() => {});

    getAllIndexPage(
      (value) => {
        applyCache(
          "categoryIndex",
          setCategoryIndex,
          value
        );
      }
    );

    axiosInstance
      .get(
        "/category-index/getByCategoryIndexId/2?page=1&pageSize=100"
      )
      .then((res) => {
        const rows =
          Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        applyCache(
          "fullFlashSale",
          setFullFlashSale,
          rows
        );
      })
      .catch(() => {});

    getAllBlog(
      "page=1&pageSize=8",
      (value) => {
        applyCache(
          "blog",
          setBlog,
          value
        );
      }
    );

    return () => {
      active = false;
    };
  }, []);


  useEffect(() => {
    let active = true;

    getRandomIndexPage(
      `page=1&pageSize=${itemsToShow}`,
      (value) => {
        if (!active) return;

        setRandomIndex(value);

        writeHomeCache(
          "randomIndexMobile",
          value
        );
      }
    );

    return () => {
      active = false;
    };
  }, [itemsToShow]);

  const categories = useMemo(
    () => [...(category?.data || [])],
    [category]
  );

  const categoriesDisplay = useMemo(
    () =>
      uniqueCategories(categories)
        .slice(0, 8),
    [categories]
  );

  const sections = useMemo(
    () => [...(categoryIndex?.data || [])],
    [categoryIndex]
  );

  const heroBanners = useMemo(
    () =>
      remoteBanner.filter(
        (item) =>
          (item?.index_page_position || "hero") === "hero" &&
          Number(item?.index_page_visible ?? 1) !== 0 &&
          bannerImage(item)
      ),
    [remoteBanner]
  );

  const promoBanners = useMemo(
    () =>
      remoteBanner.filter(
        (item) =>
          item?.index_page_position === "promo" &&
          Number(item?.index_page_visible ?? 1) !== 0 &&
          bannerImage(item)
      ),
    [remoteBanner]
  );

  const topDealSection = useMemo(
    () =>
      sections.find((section) => {
        const name = normalize(
          section?.category_index_name
        );

        return (
          name.includes("top deal") ||
          name.includes("topdeal") ||
          Number(section?.category_index_id) === 1
        );
      }),
    [sections]
  );

  const flashSaleSection = useMemo(
    () =>
      sections.find((section) => {
        const name = normalize(
          section?.category_index_name
        );

        return (
          name.includes("flash sale") ||
          name.includes("flashsale") ||
          Number(section?.category_index_id) === 2
        );
      }),
    [sections]
  );

  const allSectionProducts = useMemo(
    () =>
      uniqueProducts(
        sections.flatMap(
          (section) =>
            section?.product || []
        )
      ),
    [sections]
  );

  const topDealProducts = useMemo(
    () =>
      uniqueProducts(
        topDealSection?.product || []
      ).slice(0, 12),
    [topDealSection]
  );

  const flashSaleProducts = useMemo(
    () =>
      uniqueProducts(
        fullFlashSale.length > 0
          ? fullFlashSale
          : (flashSaleSection?.product || [])
      ).slice(0, 100),
    [fullFlashSale, flashSaleSection]
  );

  const latestProducts = useMemo(
    () =>
      newestProducts(
        allSectionProducts
      ).slice(0, 12),
    [allSectionProducts]
  );

  const latestBlogs = useMemo(
    () =>
      newestBlogs(
        blog?.data || []
      ).slice(0, 6),
    [blog]
  );

  return (
    <main className="yk6-home cyk-mobile-full">

      {heroBanners.length > 0 && (
        <section className="cyk-mobile-banners">
          {heroBanners.map((banner) => (
            <a
              key={banner.index_page_id}
              href={bannerLink(banner)}
            >
              <img
                src={bannerImage(banner)}
                alt="Y KHOA"
              />
            </a>
          ))}
        </section>
      )}

      <section className="yk6-category-grid">
        {categoriesDisplay.map((item, index) => (
          <a
            key={item?.category_slug || index}
            href={`/danh-muc-san-pham/${item?.category_slug}`}
            className="yk6-category-item"
          >
            <span className="yk6-category-image">
              <img
                src={
                  item?.category_image ||
                  categoryIcon(item?.category_name)
                }
                alt=""
              />
            </span>

            <strong>
              {shortCategoryName(item?.category_name)}
            </strong>
          </a>
        ))}
      </section>

      <section className="yk6-services">
        <div>
          <SafetyCertificateOutlined />
          <span>
            <strong>100% chính hãng</strong>
            <small>Nguồn gốc rõ ràng</small>
          </span>
        </div>

        <div>
          <TruckOutlined />
          <span>
            <strong>Giao nhanh</strong>
            <small>Toàn quốc</small>
          </span>
        </div>

        <div>
          <CustomerServiceOutlined />
          <span>
            <strong>Hỗ trợ 24/7</strong>
            <small>Tư vấn tận tâm</small>
          </span>
        </div>
      </section>

      {promoBanners.length > 0 && (
        <section className="cyk-mobile-promo">
          {promoBanners.map((banner) => (
            <a
              key={banner.index_page_id}
              href={bannerLink(banner)}
            >
              <img
                src={bannerImage(banner)}
                alt="Ưu đãi Y KHOA"
              />
            </a>
          ))}
        </section>
      )}

      {/* 6. SAN PHAM NOI BAT - TOP DEAL */}
      {topDealProducts.length > 0 && (
        <section className="yk6-section">
          <header className="yk6-heading">
            <div>
              
              <h2>Sản phẩm nổi bật</h2>
            </div>

            <a
              href={`/danh-muc-trang-chu/${topDealSection?.category_index_id}`}
            >
              Xem tất cả →
            </a>
          </header>

          <div className="cyk-horizontal-products">
            <ItemProduct
              product={topDealProducts}
              grid={5}
            />
          </div>
        </section>
      )}

      {/* 7. FLASH SALE */}
      {flashSaleProducts.length > 0 && (
        <section className="yk6-section cyk-flash-section">
          <header className="yk6-heading cyk-flash-heading">
            <div>
              

              <div className="cyk-flash-title-line">
                <h2>Khuyến mãi hôm nay</h2>

                <span className="cyk-flash-clock">
                  <span>◷</span>
                  {saleClock}
                </span>
              </div>
            </div>

            <a
              href={`/danh-muc-trang-chu/${flashSaleSection?.category_index_id}`}
            >
              Xem tất cả →
            </a>
          </header>

          <div className="cyk-horizontal-products">
            <ItemProduct
              product={flashSaleProducts}
              grid={5}
            />
          </div>
        </section>
      )}

      {/* 8. SAN PHAM MOI NHAT */}
      {latestProducts.length > 0 && (
        <section className="yk6-section">
          <header className="yk6-heading">
            <div>
              
              <h2>Gợi ý hôm nay</h2>
            </div>

            <a href="/san-pham-moi">
              Xem tất cả →
            </a>
          </header>

          <div className="cyk-horizontal-products">
            <ItemProductGrid
              product={latestProducts}
              grid={5}
            />
          </div>
        </section>
      )}

      {/* 9. BAI VIET MOI NHAT */}
      <section className="yk6-section">
        <header className="yk6-heading">
          <div>
            
            <h2>Bài viết mới nhất</h2>
          </div>

          <a href="/blog">
            Xem tất cả →
          </a>
        </header>

        {latestBlogs.length > 0 ? (
          <ItemBlog
            blog={latestBlogs}
          />
        ) : (
          <Empty />
        )}
      </section>

      <section className="yk6-newsletter">
        <div className="yk6-mail">✉</div>

        <div>
          <strong>Nhận ưu đãi mới nhất</strong>
          <span>
            Sản phẩm và thông tin mới từ Y KHOA
          </span>
        </div>

        <a href="/dang-ky">
          Đăng ký
        </a>
      </section>

    </main>
  );
}
